import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "typeSelector",
    "fields",
    "mapBlock",
    "category",
    "locationType",
    "withLocationButton",
    "noLocationButton",
    "initialUploader",
    "photoUploader"
  ]

  connect() {
    this.formValidationTargetName = null
    this.applyType(this.locationTypeTarget.value)
    this.initialMedia = null
    this.hadAdditionalPhotos = false
    this.initialRestorePending = true
    this.singleUploadReady = false
    this.photoPreviewReady = false
  }

  onSingleUploadConnected() {
    this.singleUploadReady = true
    this.tryRestoreMediaState()
  }

  onPhotoPreviewConnected() {
    this.photoPreviewReady = true
    this.tryRestoreMediaState()
  }

  tryRestoreMediaState() {
    if (!this.initialRestorePending) return
    if (!this.singleUploadReady || !this.photoPreviewReady) return

    this.initialRestorePending = false
    this.restoreMediaState()
  }

  restoreMediaState() {
    const singleController = this.singleUploadController
    const photoController = this.photoPreviewController
    if (!singleController || !photoController) return

    if (singleController.existingValue?.signed_id) {
      this.initialMedia = singleController.existingValue

      if (this.initialMedia.content_type?.startsWith("image/")) {
        this.hadAdditionalPhotos = photoController.totalCount > 0
        this.showPhotoUploader()
        photoController.setCompanionCount(1)
      } else {
        this.hadAdditionalPhotos = false
        photoController.resetPhotos()
        this.hidePhotoUploader()
      }
      this.syncManagedHiddenInputs()
      return
    }

    if (photoController.totalCount > 0) {
      const first = photoController.existingPhotos.shift()
      singleController.existingValue = first
      singleController.render()
      this.initialMedia = first
      photoController.render()
      this.hadAdditionalPhotos = true
      this.showPhotoUploader()
      photoController.setCompanionCount(1)
      this.syncManagedHiddenInputs()
    }
  }

  onInitialUploaded(event) {
    const existing = event.detail
    if (!existing?.signed_id) return

    this.initialMedia = existing
    this.hadAdditionalPhotos = false

    if (existing.content_type?.startsWith("image/")) {
      this.showPhotoUploader()
      this.photoPreviewController?.setCompanionCount(1)
    } else {
      this.photoPreviewController?.resetPhotos()
      this.hidePhotoUploader()
    }
    this.syncManagedHiddenInputs()
  }

  onInitialRemoved() {
    if (!this.initialMedia?.content_type?.startsWith("image/")) {
      this.resetToDropzone()
      return
    }

    const photoController = this.photoPreviewController
    if (photoController && photoController.totalCount > 0) {
      const promoted = photoController.existingPhotos.shift()
      const singleController = this.singleUploadController
      if (singleController) {
        singleController.existingValue = promoted
        singleController.revokeCurrentObjectUrl()
        singleController.render()
      }
      this.initialMedia = promoted
      photoController.render()
      this.hadAdditionalPhotos = true
      this.showPhotoUploader()
      photoController.setCompanionCount(1)
    } else {
      this.resetToDropzone()
    }
    this.syncManagedHiddenInputs()
  }

  onPhotosChanged() {
    const photoController = this.photoPreviewController
    if (!photoController) return

    if (photoController.totalCount > 0) {
      this.hadAdditionalPhotos = true
      this.showPhotoUploader()
      photoController.setCompanionCount(1)
    } else if (this.initialMedia?.content_type?.startsWith("image/") && !this.hadAdditionalPhotos) {
      this.showPhotoUploader()
      photoController.setCompanionCount(1)
    } else {
      this.hidePhotoUploader()
      photoController.setCompanionCount(0)
    }
    this.syncManagedHiddenInputs()
  }

  showPhotoUploader() {
    if (this.hasPhotoUploaderTarget) this.photoUploaderTarget.classList.remove("hidden")
  }

  hidePhotoUploader() {
    if (this.hasPhotoUploaderTarget) this.photoUploaderTarget.classList.add("hidden")
  }

  resetToDropzone() {
    const singleController = this.singleUploadController
    if (singleController) {
      singleController.existingValue = {}
      singleController.revokeCurrentObjectUrl()
      singleController.render()
    }
    this.initialMedia = null
    this.hadAdditionalPhotos = false
    this.photoPreviewController?.resetPhotos()
    this.hidePhotoUploader()
  }

  syncManagedHiddenInputs() {
    if (!this.hasInitialUploaderTarget) return

    this.element.querySelectorAll("input[type='hidden'][data-post-form-managed]").forEach((el) => el.remove())

    if (this.initialMedia?.content_type?.startsWith("video/")) {
      this.appendManagedInput(this.initialUploaderTarget, `${this.paramScope}[media]`, this.initialMedia.signed_id)
      return
    }

    if (!this.initialMedia?.content_type?.startsWith("image/") || !this.hasPhotoUploaderTarget) return

    const photoController = this.photoPreviewController
    if (!photoController) return

    const container = photoController.removePhotoIdsTarget || this.photoUploaderTarget
    this.appendManagedInput(container, `${this.paramScope}[photos][]`, this.initialMedia.signed_id)

    photoController.existingPhotos.forEach((photo) => {
      this.appendManagedInput(container, `${this.paramScope}[photos][]`, photo.signed_id)
    })
  }

  appendManagedInput(container, name, signedId) {
    if (!container || !signedId) return
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = signedId
    input.dataset.signedId = signedId
    input.dataset.postFormManaged = "true"
    container.appendChild(input)
  }

  get singleUploadController() {
    if (!this.hasInitialUploaderTarget) return null
    const el = this.initialUploaderTarget.querySelector('[data-controller~="single-upload"]')
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "single-upload")
  }

  get photoPreviewController() {
    if (!this.hasPhotoUploaderTarget) return null
    const el = this.photoUploaderTarget.querySelector('[data-controller~="photo-preview"]')
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "photo-preview")
  }

  get paramScope() {
    return "post"
  }

  selectWithLocation() {
    this.applyType("with_location")
  }

  selectNoLocation() {
    this.applyType("no_location")
  }

  applyType(type) {
    this.locationTypeTarget.value = type

    if (type === "") {
      this.fieldsTarget.classList.add("hidden")
      if (this.hasMapBlockTarget) this.mapBlockTarget.classList.add("hidden")
      this.toggleMapValidation(false)
      this.updateButtonStyles(false, false)
      return
    }

    this.fieldsTarget.classList.remove("hidden")
    this.updateButtonStyles(type === "with_location", type === "no_location")

    if (type === "with_location") {
      if (this.hasMapBlockTarget) {
        this.mapBlockTarget.classList.remove("hidden")
        this.resizeMap()
      }
      this.toggleMapValidation(true)
    } else {
      if (this.hasMapBlockTarget) this.mapBlockTarget.classList.add("hidden")
      this.toggleMapValidation(false)
    }

    this.filterCategories(type)
  }

  updateButtonStyles(withLocationSelected, noLocationSelected) {
    this.toggleSelected(this.withLocationButtonTarget, withLocationSelected)
    this.toggleSelected(this.noLocationButtonTarget, noLocationSelected)
  }

  toggleSelected(button, selected) {
    if (selected) {
      button.classList.add("ring-2", "ring-[var(--color-primary)]", "border-[var(--color-primary)]", "bg-blue-50")
      button.classList.remove("border-gray-200", "bg-white")
    } else {
      button.classList.remove("ring-2", "ring-[var(--color-primary)]", "border-[var(--color-primary)]", "bg-blue-50")
      button.classList.add("border-gray-200", "bg-white")
    }
  }

  filterCategories(type) {
    const options = Array.from(this.categoryTarget.options)
    options.forEach((option) => {
      if (!option.value) return
      const requiresLocation = option.dataset.requiresLocation === "true"
      option.hidden = (type === "no_location" && requiresLocation)
    })

    const selected = this.categoryTarget.selectedOptions[0]
    if (selected && selected.hidden) {
      this.categoryTarget.value = ""
      this.categoryTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    this.categoryTarget.closest('[data-controller="select-field"]')
      ?.dispatchEvent(new CustomEvent("select-field:rebuild"))
  }

  resizeMap() {
    if (!this.hasMapBlockTarget) return

    const container = this.mapBlockTarget.querySelector('[data-yandex-map-form-target="container"]')
    if (container && container.map) {
      container.map.container.fitToViewport()
    }
  }

  toggleMapValidation(required) {
    if (!this.hasMapBlockTarget) return

    if (required) {
      if (this.formValidationTargetName) {
        this.mapBlockTarget.setAttribute("data-form-validation-target", this.formValidationTargetName)
      }
    } else {
      const current = this.mapBlockTarget.getAttribute("data-form-validation-target")
      if (current) this.formValidationTargetName = current
      this.mapBlockTarget.removeAttribute("data-form-validation-target")
    }
  }
}
